import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ChevronLeft,
    Sparkles,
    Zap,
    IdCard,
    Smartphone,
    ShieldCheck,
    KeyRound,
} from "lucide-react";
import { Loader2 } from "lucide-react";

import { journeyAction } from "../../../api/journey.api";
import { setJourney } from "../../../store/journeySlice";

import MobileFrame from "../../../components/MobileFrame";

import type { RootState } from "../../../store";
import { ApiError } from "../../../api/ApiError";

interface KycOptionsProps {
    onBack: () => void;
    onSelectCkyc: () => void;
}

const KycOptions = ({
    onBack,
    onSelectCkyc,
}: KycOptionsProps) => {

    const dispatch = useDispatch();

    const [isSendingCkycOtp, setIsSendingCkycOtp] =
        useState(false);

    const [error, setError] =
        useState("");

    const [isCkycDisabled, setIsCkycDisabled] =
        useState(false);
    /*
     * ---------------------------------------------------------
     * JOURNEY
     * ---------------------------------------------------------
     *
     * Redux is the source of truth.
     *
     * Backend response:
     *
     * KYC.availableSubSteps
     * [
     *   "CKYC",
     *   "DIGILOCKER"
     * ]
     *
     * We do NOT hardcode which options are available.
     */

    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );

    const availableSubSteps =
        journey?.steps?.KYC?.availableSubSteps ?? [];

    const hasCkyc =
        availableSubSteps.includes("CKYC");

    const hasDigiLocker =
        availableSubSteps.includes("DIGILOCKER");

    /*
     * ---------------------------------------------------------
     * GUARD
     * ---------------------------------------------------------
     */

    if (!journey) {
        return null;
    }

    /*
     * ---------------------------------------------------------
     * DIGILOCKER
     * ---------------------------------------------------------
     *
     * DigiLocker flow is NOT implemented yet because the
     * DigiLocker status API contract is still pending.
     *
     * Therefore:
     * - Card is displayed when backend makes it available.
     * - Clicking it currently does nothing.
     */

    const [isGeneratingDigiLocker, setIsGeneratingDigiLocker] =
        useState(false);

    const handleDigiLockerClick = async () => {
        if (isGeneratingDigiLocker) {
            return;
        }

        try {
            setError("");
            setIsGeneratingDigiLocker(true);

            const response = await journeyAction({
                subStep: "DIGILOCKER",
                action: "GENERATE",
                payload: {
                    redirectUrl:
                        `https://stg-native.cleverpe.com/ndashboard/kyc/digilocker/callback`,
                },
            });

            /*
             * Backend is the source of truth.
             */
            dispatch(
                setJourney(
                    response.data.journey
                )
            );

            const digiLockerLink =
                response.data.metadata?.link;

            if (!digiLockerLink) {
                throw new Error(
                    "Unable to generate DigiLocker link."
                );
            }

            /*
             * Redirect user to DigiLocker.
             */
            window.location.href = digiLockerLink;

        } catch (error) {

            let message =
                "Unable to start DigiLocker verification.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setError(message);
        } finally {

            setIsGeneratingDigiLocker(false);
        }
    };

    const handleCkycClick = async () => {
        if (
            isSendingCkycOtp ||
            isCkycDisabled
        ) {
            return;
        }

        try {
            setError("");
            setIsSendingCkycOtp(true);

            const response = await journeyAction({
                subStep: "CKYC",
                action: "SEND_OTP",
                payload: {},
            });

            /*
             * Backend journey is the source of truth.
             */
            dispatch(
                setJourney(
                    response.data.journey
                )
            );

            /*
             * OTP sent successfully.
             */
            onSelectCkyc();

        } catch (error) {

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    setError(
                        "Something went wrong. Please try again later."
                    );
                    return;
                }

                const message =
                    error.message
                        ?.trim()
                        .toLowerCase() || "";

                if (
                    message.includes("ckyc number not found") ||
                    message.includes("ckyc not found") ||
                    message.includes("ckyc record not found")
                ) {
                    setError(
                        "We couldn't verify your details. Please try with DigiLocker."
                    );

                    setIsCkycDisabled(true);

                } else {
                    setError(
                        error.message?.trim() ||
                        "Unable to send CKYC OTP. Please try with DigiLocker."
                    );
                }

            } else {

                setError(
                    "Unable to send CKYC OTP. Please try with DigiLocker."
                );
            }

        } finally {
            setIsSendingCkycOtp(false);
        }
    };

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background blobs */}
                <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />

                <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">

                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <div className="flex items-center justify-between">

                        <div className="flex items-center gap-3">

                            <button
                                onClick={onBack}
                                className="
                                    flex h-9 w-9
                                    items-center justify-center
                                    rounded-full
                                    border border-slate-200
                                    bg-white
                                    text-gray-600
                                    shadow-sm
                                    active:scale-95
                                    transition-transform
                                "
                            >
                                <ChevronLeft className="h-4.5 w-4.5" />
                            </button>

                            <p className="text-base font-bold text-gray-900">
                                Complete KYC
                            </p>

                        </div>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90 -mt-3"
                        />

                    </div>


                    {/* =================================================
                        DESCRIPTION
                    ================================================= */}

                    <p className="mt-7 text-[13px] text-gray-500 leading-snug">
                        Choose how you'd like to verify your identity to
                        continue your application
                    </p>


                    {/* =================================================
                        KYC OPTIONS
                    ================================================= */}

                    <div className="mt-8 space-y-6">

                        {/* =================================================
                            DIGILOCKER
                        ================================================= */}

                        {hasDigiLocker && (
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={handleDigiLockerClick}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === "Enter" ||
                                        e.key === " "
                                    ) {
                                        handleDigiLockerClick();
                                    }
                                }}
                                className="
                                    group
                                    relative
                                    w-full
                                    text-left
                                    rounded-2xl
                                    border
                                    border-indigo-300
                                    bg-white
                                    p-5
                                    cursor-pointer

                                    ring-1
                                    ring-indigo-100/70

                                    shadow-[0_4px_12px_rgba(15,23,42,0.06),0_10px_24px_rgba(15,23,42,0.05)]

                                    transition-all
                                    duration-300
                                    hover:-translate-y-1
                                    hover:border-indigo-400
                                    hover:ring-indigo-200
                                    hover:shadow-[0_8px_20px_rgba(15,23,42,0.08),0_18px_40px_rgba(15,23,42,0.10)]

                                    active:scale-[0.98]
                                "
                            >

                                {/* Recommended badge */}
                                <span
                                    className="
                                        absolute
                                        -top-2.5
                                        right-4
                                        flex
                                        items-center
                                        gap-1
                                        whitespace-nowrap
                                        rounded-full
                                        border
                                        border-indigo-200
                                        bg-indigo-50
                                        px-2.5
                                        py-1
                                        text-[10px]
                                        font-semibold
                                        text-indigo-600
                                        shadow-sm
                                    "
                                >
                                    <Sparkles className="h-3 w-3" />
                                    Recommended
                                </span>


                                {/* Title */}
                                <div className="relative z-10 flex items-center gap-3.5">

                                    <div className="min-w-0 flex-1">

                                        <h3 className="text-[15px] font-bold text-gray-900 leading-tight">
                                            DigiLocker
                                        </h3>

                                        <p className="mt-0.5 text-[11px] text-gray-500 leading-tight">
                                            Govt. of India document wallet
                                        </p>

                                    </div>

                                </div>


                                {/* Tags */}
                                <div className="relative z-10 mt-4 flex flex-nowrap items-center gap-1.5 overflow-hidden">

                                    <span
                                        className="
                                            inline-flex
                                            shrink-0
                                            items-center
                                            gap-1
                                            whitespace-nowrap
                                            rounded-full
                                            bg-emerald-50
                                            px-2
                                            py-1
                                            text-[11px]
                                            font-medium
                                            text-emerald-600
                                        "
                                    >
                                        <ShieldCheck className="h-3.5 w-3.5" />
                                        Govt-verified
                                    </span>

                                    <span
                                        className="
                                            inline-flex
                                            shrink-0
                                            items-center
                                            gap-1
                                            whitespace-nowrap
                                            rounded-full
                                            bg-slate-50
                                            px-2
                                            py-1
                                            text-[11px]
                                            font-medium
                                            text-slate-600
                                        "
                                    >
                                        <KeyRound className="h-3.5 w-3.5" />
                                        DigiLocker PIN
                                    </span>

                                    <span
                                        className="
                                            inline-flex
                                            shrink-0
                                            items-center
                                            gap-1
                                            whitespace-nowrap
                                            rounded-full
                                            bg-slate-50
                                            px-2
                                            py-1
                                            text-[11px]
                                            font-medium
                                            text-slate-600
                                        "
                                    >
                                        <Smartphone className="h-3.5 w-3.5" />
                                        Aadhaar OTP
                                    </span>

                                </div>


                                {/* Button */}
                                <span
                                    className={`
        relative z-10
        mt-4
        flex
        w-full
        items-center
        justify-center
        gap-2
        rounded-full
        bg-gradient-to-r
        from-indigo-500
        to-blue-400
        py-2.5
        text-[13px]
        font-semibold
        text-white
        shadow-sm
        transition-all
        ${isGeneratingDigiLocker
                                            ? "opacity-50 cursor-not-allowed"
                                            : "cursor-pointer"
                                        }
    `}
                                >
                                    {isGeneratingDigiLocker && (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    )}

                                    {isGeneratingDigiLocker
                                        ? "Opening DigiLocker..."
                                        : "Continue with DigiLocker"}
                                </span>

                            </div>
                        )}


                        {/* =================================================
                            CKYC
                        ================================================= */}

                        {hasCkyc && (
                            <div
                                role="button"
                                tabIndex={isCkycDisabled ? -1 : 0}
                                aria-disabled={isCkycDisabled}
                                onClick={() => {
                                    if (!isCkycDisabled) {
                                        handleCkycClick();
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (
                                        !isCkycDisabled &&
                                        (e.key === "Enter" || e.key === " ")
                                    ) {
                                        e.preventDefault();
                                        handleCkycClick();
                                    }
                                }}
                                className={`
        group
        relative
        w-full
        text-left
        rounded-2xl
        border
        border-slate-200
        bg-white
        p-5
        transition-all
        duration-300

        ${isCkycDisabled
                                        ? "cursor-not-allowed opacity-40 grayscale blur-[0.5px] pointer-events-none"
                                        : `
                    cursor-pointer
                    shadow-[0_4px_12px_rgba(15,23,42,0.06),0_10px_24px_rgba(15,23,42,0.05)]
                    hover:-translate-y-1
                    hover:border-slate-300
                    hover:shadow-[0_8px_20px_rgba(15,23,42,0.08),0_18px_40px_rgba(15,23,42,0.10)]
                    active:scale-[0.98]
                `
                                    }
    `}
                            >

                                {/* Title */}
                                <div className="relative z-10 flex items-center gap-3.5">

                                    <div className="min-w-0 flex-1">

                                        <h3 className="text-[15px] font-bold text-gray-900 leading-tight">
                                            CKYC
                                        </h3>

                                        <p className="mt-0.5 text-[11px] text-gray-500 leading-tight">
                                            Central KYC Registry lookup
                                        </p>

                                    </div>

                                </div>


                                {/* Tags */}
                                <div className="relative z-10 mt-4 flex flex-nowrap items-center gap-1.5 overflow-hidden">

                                    <span
                                        className="
                                            inline-flex
                                            shrink-0
                                            items-center
                                            gap-1
                                            whitespace-nowrap
                                            rounded-full
                                            bg-emerald-50
                                            px-2
                                            py-1
                                            text-[11px]
                                            font-medium
                                            text-emerald-600
                                        "
                                    >
                                        <Zap className="h-3.5 w-3.5" />
                                        Fastest
                                    </span>

                                    <span
                                        className="
                                            inline-flex
                                            shrink-0
                                            items-center
                                            gap-1
                                            whitespace-nowrap
                                            rounded-full
                                            bg-slate-50
                                            px-2
                                            py-1
                                            text-[11px]
                                            font-medium
                                            text-slate-600
                                        "
                                    >
                                        <IdCard className="h-3.5 w-3.5" />
                                        CKYC Registered PAN
                                    </span>

                                    <span
                                        className="
                                            inline-flex
                                            shrink-0
                                            items-center
                                            gap-1
                                            whitespace-nowrap
                                            rounded-full
                                            bg-slate-50
                                            px-2
                                            py-1
                                            text-[11px]
                                            font-medium
                                            text-slate-600
                                        "
                                    >
                                        <Smartphone className="h-3.5 w-3.5" />
                                        OTP
                                    </span>

                                </div>


                                {/* Button */}
                                <span
                                    className={`
        relative z-10
        mt-4
        flex
        w-full
        items-center
        justify-center
        gap-2
        rounded-full
        py-2.5
        text-[13px]
        font-semibold
        transition-all

        ${isCkycDisabled
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            : `
                    bg-gradient-to-r
                    from-indigo-500
                    to-blue-400
                    text-white
                    shadow-sm
                `
                                        }

        ${isSendingCkycOtp
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                        }
    `}
                                >
                                    {isSendingCkycOtp && (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    )}

                                    {isCkycDisabled
                                        ? "CKYC Unavailable"
                                        : isSendingCkycOtp
                                            ? "Sending OTP..."
                                            : "Request CKYC OTP"}
                                </span>

                            </div>
                        )}

                    </div>

                    {/* Error Message */}
                    {error && (
                        <div
                            role="alert"
                            aria-live="assertive"
                            className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
                        >
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100">
                                <span className="text-[11px] font-bold text-red-600">
                                    !
                                </span>
                            </div>

                            <p className="flex-1 text-left text-[11px] font-medium text-red-600">
                                {error}
                            </p>
                        </div>
                    )}


                    {/* Spacer */}
                    <div className="flex-1" />


                    {/* Footer */}
                    <p className="mb-1 text-center text-[11px] font-semibold tracking-wide text-gray-400">
                        CleverPe
                    </p>

                </div>

            </div>
        </MobileFrame>
    );
};

export default KycOptions;